<template>
  <div style="display: contents" role="rowgroup">
    <tr
      :class="{
        alert: error
      }"
      :style="!error ? 'background-color: #f6f6f6' : ''"
    >
      <template v-if="$vuetify.breakpoint.mdAndUp">
        <td>
          {{ $dayjs.date(item.createdAt) }}
          <div class="muted--text">
            {{ $dayjs(item.createdAt).format("HH:mm:ss") }}
          </div>
        </td>
        <td>
          {{ $t(reason, item.reason) }}
          <br />
          {{ item.referenceCode }}
          <br />
          {{ $t("common:payment.processor." + item.processor) }}
        </td>
        <td>
          <AppShowEventDetails
            v-if="item.fromEvent"
            :event="item.fromEvent"
            link
            small
          />
        </td>
        <td>
          <AppShowPayoutStatus :value="item" with-details />
        </td>
        <td>
          <div class="accent--text currency-display">
            <b>{{ masterCurrency(item.amountCents) }}</b>
          </div>
        </td>
        <td>
          <div v-if="item.status == 'PENDING'">
            <v-tooltip
              v-if="$allowedMutation(item.allowedMutations, 'paymentConfirm')"
              bottom
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  :aria-label="
                    $t(
                      'people:ShoppingBasket.TransactionHistory.confirmPaymentButton.alt'
                    )
                  "
                  v-bind="attrs"
                  :disabled="mutationLoading"
                  class="edit-green"
                  v-on="on"
                  @click="$emit('confirm', { item: item, evt: $event })"
                >
                  <v-icon color="white" small>$iconCheck</v-icon>
                  <v-progress-circular
                    v-show="false"
                    color="white"
                    size="21"
                    indeterminate
                  />
                </v-btn>
              </template>
              <span>
                {{
                  $t(
                    "people:ShoppingBasket.TransactionHistory.confirmPaymentButton.text",
                    "Confirm payment"
                  )
                }}
              </span>
            </v-tooltip>

            <v-tooltip
              v-if="$allowedMutation(item.allowedMutations, 'paymentCancel')"
              bottom
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  class="remove"
                  :aria-label="
                    $t(
                      'people:ShoppingBasket.TransactionHistory.cancelPaymentButton.alt'
                    )
                  "
                  :disabled="mutationLoading"
                  v-on="on"
                  @click="$emit('cancel', { item: item, evt: $event })"
                >
                  <v-icon color="white" small>$iconTimesCircle</v-icon>
                  <v-progress-circular
                    v-show="false"
                    color="white"
                    size="21"
                    indeterminate
                  />
                </v-btn>
              </template>
              <span>
                {{
                  $t(
                    "people:ShoppingBasket.TransactionHistory.CancelPaymentButton.label",
                    "Cancel payment",
                    "Label of cancel payment button in Transaction History tab"
                  )
                }}
              </span>
            </v-tooltip>
          </div>
        </td>
      </template>
      <template v-else>
        <td>
          {{ $dayjs.date(statusDate()) }}
          <br />
          <span class="muted--text">
            {{ $dayjs(statusDate()).format("HH:mm:ss") }}
          </span>
        </td>
        <td>
          {{ $t(reason, item.reason) }}
          <br />
          {{ item.referenceCode }}
          <br />
          <span class="muted--text">{{
            $t("common:payment.processor." + item.processor)
          }}</span>
        </td>
        <td>
          <div class="text-center accent--text currency-display">
            <b>{{ masterCurrency(item.amountCents) }}</b>
          </div>
          <div class="text-center">
            <AppShowPayoutStatus :value="item" />
          </div>
        </td>
        <td class="px-0">
          <button
            class="btn-focus"
            :aria-label="$t('common:expandButton.alt')"
            @click.prevent="expand(!isExpanded)"
          >
            <v-icon color="primary">{{
              isExpanded
                ? "$iconCircleCaretUp"
                : "$iconCircleCaretDown"
            }}</v-icon>
          </button>
        </td>
      </template>
    </tr>
    <PayoutHistoryErrorRow v-if="error" :item="item" />
  </div>
</template>
<script>
import { useCurrency } from "@composables/masterdata/config";
import AppShowEventDetails from "@/components/elements/AppShows/event/AppShowEventDetails.vue";
import AppShowPayoutStatus from "@/components/elements/AppShows/order/AppShowPayoutStatus.vue";
//import AppReceiptLink from "@/components/elements/AppReceiptLink.vue";
import PayoutHistoryErrorRow from "@AppShows/order/PayoutHistoryErrorRow.vue";
export default {
  name: "PayoutHistory",
  components: {
    AppShowEventDetails,
    AppShowPayoutStatus,
    //AppReceiptLink,
    PayoutHistoryErrorRow
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    transactionId: {
      type: String,
      required: true
    },
    mutationLoading: {
      type: Boolean,
      default: false
    },
    expand: {
      type: Function,
      default: () => {}
    },
    isExpanded: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    return {
      ...useCurrency()
    };
  },
  computed: {
    error: ({ item }) =>
      item.status == "PENDING" && item.processingResolution == "USER_ERROR",
    reason: ({ item: i }) => {
      switch (i.reason) {
        case "RETURNS":
          return "wallet:payout.reason.Returns.label";
        case "PRIZE_MONEY":
          return "wallet:payout.reason.PrizeMoney.label";
        default:
          return `wallet:payout.reason.${i.reason}.label`;
      }
    },
    displayDate() {
      let date = "";
      if (this.item.status == "PENDING") {
        date = this.$dayjs.date(this.item.createdAt);
      } else if (this.item.status == "PAID") {
        date = this.$dayjs.date(this.item.confirmedAt);
      } else if (this.item.status == "CANCELLED") {
        date = this.$dayjs.date(this.item.createdAt);
      }
      return date;
    }
  },
  methods: {
    statusDate() {
      switch (this.item.status) {
        case "PENDING":
          return this.item.createdAt;
        case "PAID":
          return this.item.confirmedAt;
        default:
          return this.item.createdAt;
      }
    }
  }
};
</script>
<style scoped lang="scss">
@media (max-width: 959px) {
  tr td {
    font-size: 12px !important;
  }
}
</style>
